/*
 * - Sets up instance of axios
 * - Consumes axios instance on request function
 * - Request function is a wrapper around axios that handles success and error responses
 */

import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { authStore } from '@stores';
import { displayMessages } from './Request.messages';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
}

export const REQUEST_METHODS = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  DELETE: 'delete',
  PATCH: 'patch',
};

const PROXIED_PATHS = ['/ribits', '/AORService'];

const isProxiedPath = (url?: string) => !!url && PROXIED_PATHS.some((path) => url.startsWith(path));

export const client = (() =>
  axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      Accept: 'application/json, text/plain, */*',
    },
  }))();

client.interceptors.request.use(
  (customConfig: InternalAxiosRequestConfig & { skipAuth?: boolean }) => {
    const accessToken = authStore.getAccessToken();
    if (!customConfig.skipAuth && accessToken) {
      customConfig.headers = customConfig.headers ?? {};
      customConfig.headers.Authorization = `Bearer ${accessToken}`;
    }

    return customConfig;
  },
  (error: AxiosError) => Promise.reject(error)
);

client.interceptors.response.use(
  (response: AxiosResponse) => {
    displayMessages({
      responseMessage: response.data?.msg,
      responseStatusCode: response.status,
      responseStatusText: response.data?.status,
      responseUrl: response.config?.url,
      type: 'success',
    });

    return response;
  },
  (error: AxiosError) => {
    displayMessages({
      responseMessage: error?.message,
      responseStatusCode: error?.status,
      responseStatusText: error.response?.statusText,
      responseUrl: error.config?.url,
      type: 'error',
    });

    return Promise.reject(error);
  }
);

const request = async <T = unknown>(options: CustomAxiosRequestConfig): Promise<T> => {
  if (isProxiedPath(options.url)) {
    options.baseURL = '';
  }

  const onSuccess = (response: AxiosResponse<T>) => {
    const { data } = response;
    return data;
  };

  const onError = function (error: AxiosError) {
    return Promise.reject({
      message: error.message,
      code: error.code,
      response: error.response,
    });
  };

  return client(options).then(onSuccess).catch(onError);
};

export default request;
